import { useCallback, useState } from 'react';

/**
 * A simple hook to manage the open/close state of UI components
 * like modals, drawers, popovers, etc.
 *
 * @param {boolean} [initialState=false] - Optional initial open state.
 * @returns {{
 *   isOpen: boolean,
 *   open: () => void,
 *   close: () => void,
 *   toggle: () => void
 * }} An object containing state and control functions.
 *
 * @example
 * const { isOpen, open, close, toggle } = useDisclosure();
 * open();   // sets isOpen to true
 * toggle(); // toggles isOpen
 */
function useDisclosure(initialState = false) {
  const [isOpen, setIsOpen] = useState(initialState);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  return { isOpen, open, close, toggle };
}

export default useDisclosure;
